import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import { Popover, PopoverPortalContent, PopoverTrigger } from "@/components/_atoms/popover";
import { IconDisplayOptions } from "@/components/_icons/IconDisplayOptions";

import { useUnsafeDatatableContext } from "../use-datatable-context.tsx";

import { DatatableDisplayOptionsColumnToggleView } from "./datatable-display-options-column-toggle-view.tsx";
import { DatatableDisplayOptionsHeaderVisibilitySwitch } from "./datatable-display-options-header-visibility-switch.tsx";
import { DatatableDisplayOptionsOrderingSelect } from "./datatable-display-options-ordering-select.tsx";
import { DatatableDisplayOptionsSectioningSelect } from "./datatable-display-options-sectioning-select.tsx";
import { DatatableDisplayOptionsSubsectioningSelect } from "./datatable-display-options-subsectioning-select.tsx";

const POPOVER_COLLISION_PADDING = 8;

interface DatatableDisplayOptionsButtonProps {
  hideGrouping?: boolean;
  hideSubgrouping?: boolean;
  hideSorting?: boolean;
  customContent?: ReactNode;
  hideDatatableOptions?: boolean;
  hideListAndDisplaySettings?: boolean;
}

export const DatatableDisplayOptionsButton = ({
  hideGrouping,
  hideSubgrouping,
  hideSorting,
  customContent,
  hideDatatableOptions,
  hideListAndDisplaySettings,
}: DatatableDisplayOptionsButtonProps) => {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const context = useUnsafeDatatableContext();
  const table = context?.table;
  const getConfigurationForColumn = context?.getConfigurationForColumn;
  const sectionColumnId = context?.state.sectionColumnId;
  const lock = context?.lock;

  const sectioningColumnList = useMemo(() => {
    if (!table || !getConfigurationForColumn || !lock) {
      return [];
    }
    return table
      .getAllColumns()
      .filter(
        (column) => getConfigurationForColumn(column.id)?.enableSectioning === true && lock.canOfferColumn(column),
      );
  }, [table, getConfigurationForColumn, lock]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild={true}>
        <Button
          variant="outline"
          size="xs"
          className="font-normal"
          data-testid="climate-action-list-display-options-button"
        >
          <IconDisplayOptions className="mr-2 h-3.5 w-3.5 text-foreground/60" />
          {t("view.display")}
        </Button>
      </PopoverTrigger>
      <PopoverPortalContent
        className="z-9999 max-h-(--radix-popover-content-available-height) w-80 overflow-y-auto p-2"
        side="bottom"
        align="end"
        collisionPadding={POPOVER_COLLISION_PADDING}
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement;
          const toggleGroup = target.closest('[role="group"]');
          if (toggleGroup) {
            e.preventDefault();
          }
        }}
      >
        <div className="">
          {customContent && (
            <>
              {customContent}
              {!hideDatatableOptions && context && <hr />}
            </>
          )}
          {!hideDatatableOptions && context && (
            <>
              {!hideGrouping && sectioningColumnList.length > 0 && (
                <>
                  <div className="my-2 flex flex-row items-center">
                    <div className="mb-2 mr-4 w-44 text-xs font-medium text-muted-foreground">{t("view.grouping")}</div>
                    <DatatableDisplayOptionsSectioningSelect />
                  </div>
                  {!hideSubgrouping && sectionColumnId && <DatatableDisplayOptionsSubsectioningSelect />}
                </>
              )}
              {!hideSorting && (
                <div className="my-2 flex flex-row items-center">
                  <div className="mb-2 mr-4 w-44 text-xs font-medium text-muted-foreground">{t("view.sorting")}</div>
                  <DatatableDisplayOptionsOrderingSelect />
                </div>
              )}
              {!hideListAndDisplaySettings && (
                <>
                  <hr />
                  <div className="my-2 flex flex-col gap-4">
                    <div className="flex flex-row flex-wrap items-center gap-2">
                      <div className="py-2 text-xs font-medium text-foreground/80">{t("view.listSettings")}</div>

                      <DatatableDisplayOptionsHeaderVisibilitySwitch />
                    </div>
                  </div>
                  <DatatableDisplayOptionsColumnToggleView />
                </>
              )}
            </>
          )}
        </div>
      </PopoverPortalContent>
    </Popover>
  );
};
